import React, { useState, useEffect } from "react";
import { 
  Calculator, Target, GraduationCap, Users, Globe, Clock, AlertCircle, 
  BookOpen, TrendingUp, ChevronRight 
} from "lucide-react";
import { motion } from "framer-motion";
import ContactForm from "../components/ContactForm";
import { Link } from "react-router-dom";

const IELTSPage = () => {
  const [calculator, setCalculator] = useState({
    listening: 5.5, reading: 5.5, writing: 5.5, speaking: 5.5
  });
  const [overall, setOverall] = useState(5.5);
  const [cefr, setCefr] = useState("B1");

  const bandOptions = [0,1,2,3,4,5,5.5,6,6.5,7,7.5,8,8.5,9];

  const updateScore = () => {
    const total = Object.values(calculator).reduce((a, b) => a + b, 0) / 4;
    const rounded = Math.round(total * 2) / 2; // Round to nearest 0.5
    setOverall(rounded);
    
    const cefrMap = {
      0: "Did Not Attempt", 4: "A2", 5: "B1", 5.5: "B1", 
      6: "B2", 6.5: "B2", 7: "C1", 7.5: "C1", 8: "C2"
    };
    const level = Object.keys(cefrMap).reverse().find(key => rounded >= parseFloat(key)) || "A2";
    setCefr(cefrMap[level] || level);
  };

  useEffect(() => {
    updateScore();
  }, [calculator]);

  const selfSelectCards = [
    { icon: GraduationCap, title: "University Applicants", desc: "Academic module for undergraduate/postgraduate admissions (UK, Australia, Canada, USA)" },
    { icon: Users, title: "Professional Registration", desc: "Academic module for healthcare/engineering registration (nurses, doctors, architects)" },
    { icon: TrendingUp, title: "Visa Applicants", desc: "Academic (study visas) or General Training (work/live visas) per government requirements" }
  ];

  const benchmarks = [
    { score: "6.0+", meaning: "Competent user - Many undergraduate programs", unis: "UK outside London, regional Australia, Canadian colleges" },
    { score: "6.5+", meaning: "Good user - Standard university entry", unis: "Most Australian universities, Canadian undergraduate, UK Tier 2" },
    { score: "7.0+", meaning: "Good user - Competitive programs", unis: "Russell Group, Group of Eight Australia, US liberal arts colleges" },
    { score: "7.5+", meaning: "Very good user - Selective programs", unis: "Institution-specific; often paired with high GPA" }
  ];

  const myths = [
    { text: "Any native accent scores higher", counter: "Examiners trained for all accents; scoring uses public band descriptors" },
    { text: "Speaking confidence = high band", counter: "Fluency scored with pronunciation, lexical resource, grammatical range" },
    { text: "Memorizing Task 2 essays works", counter: "Examiners identify memorized responses; topic responses must be relevant" },
    { text: "Computer IELTS scores differently", counter: "Same global scale; different delivery format, same examiner standardisation" }
  ];

  const testFormat = [
    { section: "Listening", duration: "30 min + 10 min transfer" },
    { section: "Reading (Academic)", duration: "60 min (3 long texts)" },
    { section: "Writing (Academic)", duration: "60 min", note: "Task 2 = double weight" },
    { section: "Speaking", duration: "11-14 min (3 parts)" }
  ];

  const preparationFlow = [
    { step: "Diagnostic Assessment", desc: "Full practice test with band score + CEFR mapping across 4 skills" },
    { step: "Band-Specific Roadmap", desc: "Custom plan targeting your gap (e.g., 6.0→7.0) with weekly milestones" },
    { step: "Task Response Feedback", desc: "Academic Task 1 graphs, Task 2 rubrics, pronunciation analysis" },
    { step: "Timed Simulations", desc: "Paper/computer practice tests with examiner-standard score reports" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white">
      {/* 1. Hero + Calculator */}
      <section className="py-24 px-6 md:px-12 max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <h1 className="text-5xl md:text-6xl font-serif font-light text-slate-900 leading-tight">
              IELTS Academic Overview
            </h1>
            <p className="text-xl text-slate-600 max-w-lg leading-relaxed">
              English proficiency test for higher education and professional registration worldwide. Academic version required for university admissions.
            </p>
            <p className="text-lg text-slate-500">
              Paper-based or computer-delivered formats available. Results typically released 13 days (paper) or 3-5 days (computer).
            </p>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white/80 backdrop-blur-xl rounded-3xl p-8 shadow-xl border border-slate-100 max-h-[32rem] overflow-y-auto"
          >
            <div className="flex items-center gap-3 mb-4">
              <Calculator className="w-6 h-6 text-emerald-600" />
              <h3 className="text-xl font-semibold text-slate-900">Estimate Your Overall Band</h3>
            </div>
            <p className="text-xs text-slate-500 mb-6 italic">
              This estimates simple average of section scores. Actual IELTS overall band uses weighted average with no penalty for 0.25 increments.
            </p>
            <div className="space-y-4">
              {["listening", "reading", "writing", "speaking"].map(section => (
                <div key={section} className="space-y-1">
                  <label className="text-sm font-medium text-slate-700 capitalize">
                    {section.replace(/^\w/, c => c.toUpperCase())}
                  </label>
                  <select
                    value={calculator[section]}
                    onChange={e => setCalculator({...calculator, [section]: +e.target.value})}
                    className="w-full p-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  >
                    {bandOptions.map(band => (
                      <option key={band} value={band}>{band}</option>
                    ))}
                  </select>
                </div>
              ))}
              <div className="pt-6 border-t border-slate-200">
                <div className="text-3xl font-bold text-slate-900 mb-1">Overall: {overall}</div>
                <div className="flex items-center gap-2 text-emerald-700">
                  <span className="font-semibold">{cefr}</span>
                  <span className="text-sm">(CEFR Level - used for EU/Canada benchmarking)</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. Who Takes IELTS Academic? */}
      <section className="py-24 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-serif font-light text-slate-900 mb-6">
            Who Takes IELTS Academic?
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Academic module required for university admissions. General Training used for work/migration visas. 
            <strong>Choosing the wrong module means retaking the test.</strong>
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {selfSelectCards.map((card, i) => (
            <motion.div
              key={card.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="group bg-white rounded-3xl p-10 hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 border border-slate-100 hover:border-emerald-200"
            >
              <card.icon className="w-12 h-12 text-emerald-600 mb-6 mx-auto" />
              <h3 className="text-2xl font-semibold text-slate-900 mb-4">{card.title}</h3>
              <p className="text-slate-600 leading-relaxed">{card.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 3. CTA Cluster */}
      <section className="py-24 px-6 md:px-12 bg-emerald-50 border-t border-emerald-200">
        <div className="max-w-2xl mx-auto text-center space-y-8">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-white rounded-2xl shadow-lg">
            <BookOpen className="w-8 h-8 text-emerald-600" />
            <div>
              <h3 className="text-xl font-semibold text-slate-900">Next Steps</h3>
              <p className="text-sm text-slate-600">Review preparation pathways for your target band</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/test-prep#ielts">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="bg-emerald-600 hover:bg-emerald-700 px-8 py-4 rounded-2xl text-lg font-semibold shadow-xl flex items-center gap-2"
            >
              IELTS Preparation Programs
              <ChevronRight className="w-5 h-5" />
            </motion.button>
            </Link>
            <Link to="/test-prep">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="border-2 border-slate-600 hover:border-slate-400 bg-white text-slate-900 px-8 py-4 rounded-2xl text-lg font-semibold flex items-center gap-2"
            >
              All Test Prep
            </motion.button>
            </Link>
          </div>
        </div>
      </section>

      {/* 4. Score Benchmarks */}
      <section className="py-24 px-6 md:px-12 bg-slate-50">
        <div className="max-w-4xl mx-auto text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-serif font-light text-slate-900 mb-6">
            What Your Score Means
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto mb-8">
            <strong>Band jumps above 6.5 require targeted feedback on Writing/Speaking - most candidates plateau here without structured analysis.</strong>
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
          {benchmarks.map((bench, i) => (
            <motion.div
              key={bench.score}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              className="bg-white rounded-3xl p-8 hover:shadow-xl border border-slate-100"
            >
              <div className="text-3xl font-bold text-emerald-600 mb-4">{bench.score}</div>
              <h4 className="text-xl font-semibold text-slate-900 mb-3">{bench.meaning}</h4>
              <p className="text-slate-600">{bench.unis}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 5. Global Acceptance */}
      <section className="py-24 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-serif font-light text-slate-900 mb-6">
            Global Acceptance
          </h2>
          <p className="text-xl text-slate-600">
            Recognized by 12,500+ organizations including UKVI, Australian Home Affairs, IRCC Canada, and US universities.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} className="bg-gradient-to-r from-emerald-50 to-blue-50 rounded-3xl p-10">
            <Globe className="w-16 h-16 text-emerald-600 mx-auto mb-6" />
            <h3 className="text-2xl font-semibold text-slate-900 mb-4">Study Abroad</h3>
            <p className="text-lg text-slate-600">Academic module accepted for 100% of Australian universities, 98% UK universities, major Canadian/US institutions.</p>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-3xl p-10">
            <Target className="w-16 h-16 text-blue-600 mx-auto mb-6" />
            <h3 className="text-2xl font-semibold text-slate-900 mb-4">Migration Visas</h3>
            <p className="text-lg text-slate-600">UKVI approved (Academic/General), Australia student visas (Academic), Canada Express Entry (General Training).</p>
          </motion.div>
        </div>
      </section>

      {/* 6. Common Misconceptions */}
      <section className="py-24 px-6 md:px-12 bg-slate-50">
        <div className="max-w-4xl mx-auto mb-20">
          <h2 className="text-4xl md:text-5xl font-serif font-light text-slate-900 mb-12 text-center">
            Common Misconceptions
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {myths.map((myth, i) => (
              <motion.div
                key={myth.text}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                className="flex flex-col gap-4 p-6 bg-white rounded-2xl border border-slate-200 hover:border-amber-200 group"
              >
                <div className="flex items-start gap-4">
                  <AlertCircle className="w-8 h-8 text-amber-500 flex-shrink-0 mt-1" />
                  <p className="text-slate-700 group-hover:text-slate-900 font-medium">{myth.text}</p>
                </div>
                <p className="text-sm text-slate-600 font-semibold bg-amber-50 p-3 rounded-xl">
                  {myth.counter}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. Test Structure */}
      <section className="py-24 px-6 md:px-12">
        <div className="max-w-2xl mx-auto text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-serif font-light text-slate-900 mb-6">
            Test Structure
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto mb-16">
            Total: 2 hours 45 minutes (no break between Listening/Reading). 
            <strong>Speaking: Live human examiner (same for paper/computer IELTS) - accent-neutral evaluation.</strong>
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {testFormat.map((format, i) => (
            <motion.div
              key={format.section}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              className="bg-white rounded-3xl p-8 text-center border border-slate-100 hover:shadow-lg"
            >
              <h3 className="text-xl font-semibold text-slate-900 mb-4">
                {format.section}
                {format.note && (
                  <div className="text-xs text-amber-600 font-semibold bg-amber-50 px-2 py-1 rounded-full inline-block ml-2">
                    {format.note}
                  </div>
                )}
              </h3>
              <Clock className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <div className="text-2xl font-bold text-slate-900">{format.duration}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 8. How We Help You Prepare */}
      <section className="py-24 px-6 md:px-12 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-serif font-light text-slate-900 mb-6">
            How We Help You Prepare
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Structured preparation aligned with IELTS Academic band descriptors and task rubrics.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {preparationFlow.map((step, i) => (
            <motion.div
              key={step.step}
              initial={{ opacity: 0, x: i % 2 ? 20 : -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              className="bg-white rounded-3xl p-10 border border-slate-100 hover:shadow-xl hover:border-emerald-200"
            >
              <div className="flex items-start gap-4 mb-6">
                <div className="w-10 h-10 bg-emerald-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-emerald-700">{i + 1}</span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-slate-900">{step.step}</h3>
                </div>
              </div>
              <p className="text-slate-600 leading-relaxed">{step.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 9. ContactForm */}
      <section id="contact-form" className="max-w-6xl mx-auto px-8 pb-12 bg-slate-50">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-serif font-light text-slate-900 mb-6">
            Questions About IELTS?
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Schedule consultation for band pathway advice. Scores valid 2 years. Unlimited retakes.
          </p>
        </div>
        <ContactForm />
      </section>
    </div>
  );
};

export default IELTSPage;
